import React, { useEffect, useState } from 'react';
import styles from './CompositionRecord.module.css';
import { CancelIcon, DragThumbIcon, TrashCanIcon } from 'features/compositions/assets/icons/icons';
import SwitchButton from './SwitchButton';
import useTheme from 'config/useTheme';
import RecordDropdown from './RecordDropdown';
import { validateFieldName } from 'utils/validations';
import DefaultRecordValueInput from './DefaultRecordValueInput';
import dataTypesConfig from 'features/compositions/constants/dataTypes.json';
import RecordOptionSwitch from './RecordOptionSwitch';
import { decimalProblem, MAX_DECIMAL_PRECISION } from 'features/compositions/utils/fieldTypes';

const CompositionRecord = ({
  item,
  index,
  isDragged,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDrop,
  onChange,
  onDelete,
  dataTypes,
  customTypes = [],
  isPrimaryKeyRow = false
}) => {
  const { dark } = useTheme();
  const [editMode, setEditMode] = useState(false);
  const [isFieldValid, setIsFieldValid] = useState({ name: true });
  const [tempValues, setTempValues] = useState({
    name: item.name,
    type: item.type,
    default_value: item.default_value,
    is_index: item.is_index,
    ai: item.ai,
    is_array: item.is_array,
    is_auto_created: item.is_auto_created || false,
    is_auto_updated: item.is_auto_updated || false,
    description: item.description || '',
    precision: item.precision,
    scale: item.scale,
  });

  useEffect(() => {
    if (!editMode) {
      setTempValues({
        name: item.name,
        type: item.type,
        default_value: item.default_value,
        is_index: item.is_index,
        ai: item.ai,
        is_array: item.is_array,
        is_auto_created: item.is_auto_created || false,
        is_auto_updated: item.is_auto_updated || false,
        description: item.description || '',
        precision: item.precision,
        scale: item.scale,
      });
    }
  }, [item, editMode]);

  const saveChanges = () => {
    Object.entries(tempValues).forEach(([key, val]) => {
      if (val !== item[key]) {
        onChange(index, key, val);
      }
    });
    setEditMode(false);
  };

  return (
    <div
      draggable
      onDragStart={() => onDragStart(index)}
      onDragOver={e => onDragOver(e, index)}
      onDragEnd={onDragEnd}
      onDrop={onDrop}
      className={`${styles.composition_record} ${isDragged ? styles.dragged : ''} ${dark ? styles.dark : ''} ${editMode ? styles.edit_mode : ''}`}
    >
      <div className={styles.drag_thumb}><DragThumbIcon /></div>

      <SwitchButton
        checked={!!tempValues.is_index}
        onChange={e => {
          const isIndexChecked = e.target.checked;
          setTempValues(prev => {
            if (isIndexChecked && !dataTypesConfig.indexTypes.some(opt => opt.value === prev.type)) {
              return { ...prev, is_index: true, type: 'uint64_t' };
            }
            return { ...prev, is_index: isIndexChecked };
          });
        }}
        disabled={!editMode}
      />
      <SwitchButton
        checked={!!tempValues.ai}
        onChange={e => setTempValues(prev => {
          const updated = { ...prev, ai: e.target.checked };
          // Reset default_value when AI is enabled for uint64_t index
          if (e.target.checked && prev.type === "uint64_t" && prev.is_index) {
            updated.default_value = "";
          }
          return updated;
        })}
        disabled={!editMode}
      />
             <SwitchButton
         checked={!!tempValues.is_array}
         onChange={e => setTempValues(prev => ({ ...prev, is_array: e.target.checked }))}
         disabled={!editMode || tempValues.is_index}
       />

      <div className={styles.record_col}>
        {!editMode ? (
          <span>{item?.name}</span>
        ) : (
          <input
            value={tempValues.name || ""}
            className={`${styles.edit_input_value} ${!isFieldValid.name ? styles.edit_input_value__invalid : ''}`}
            onChange={e => {
              const newName = e.target.value;
              setTempValues(prev => ({ ...prev, name: newName }));
              const isPrimaryIndexRow = (!!tempValues.is_index) && index === 0;
              const isValidName = isPrimaryIndexRow ? newName.trim().length > 0 : validateFieldName(newName);
              setIsFieldValid(prev => ({
                ...prev,
                name: isValidName
              }));
            }}
          />
        )}
      </div>

      <div className={styles.record_col}>
        {!editMode ? (
          <span>{(dataTypes?.find(opt => opt.value === item.type) || customTypes.find(ct => ct.name === item.type))?.label || item.type}</span>
        ) : (
          <RecordDropdown
            label={(dataTypes?.find(opt => opt.value === tempValues.type) || customTypes.find(ct => ct.name === tempValues.type))?.label || 'Select'}
            options={tempValues.is_index ? 
              (isPrimaryKeyRow ? dataTypesConfig.indexTypes.filter(t => t.value === 'uint64_t') : dataTypesConfig.indexTypes) : 
              dataTypes || []
            }
            onChange={opt => {
              const newType = isPrimaryKeyRow ? 'uint64_t' : opt.value;
              setTempValues(prev => {
                const updated = { ...prev, type: newType };
                // Tačna decimala traži broj cifara; bez ponuđenog početka bi
                // polje bilo prazno i kolona odmah neispravna.
                if (newType === 'decimal') {
                  if (!Number.isInteger(Number(updated.precision))) updated.precision = MAX_DECIMAL_PRECISION;
                  if (!Number.isInteger(Number(updated.scale))) updated.scale = 2;
                }
                // Reset auto created/updated if type is not time_point
                if (newType !== 'time_point') {
                  updated.is_auto_created = false;
                  updated.is_auto_updated = false;
                }
                // Reset default_value when AI is enabled for uint64_t index
                if (updated.ai && newType === "uint64_t" && updated.is_index) {
                  updated.default_value = "";
                }
                // Reset default_value when AC/AU is enabled for time_point
                if (newType === "time_point" && (updated.is_auto_created || updated.is_auto_updated)) {
                  updated.default_value = "";
                }
                return updated;
              });
            }}
          />
        )}

        {/* Tačna decimala se na lancu čuva kao ceo broj, pa joj broj cifara
            mora biti zadat ovde — posle se ne može promeniti bez ponovnog
            upisa svakog reda. */}
        {tempValues.type === 'decimal' && (
          <div className={styles.decimal_spec}>
            <input
              type="number" min="1" max={MAX_DECIMAL_PRECISION}
              value={tempValues.precision ?? ''}
              disabled={!editMode}
              title="Total digits"
              onChange={e => setTempValues(prev => ({ ...prev, precision: e.target.value === '' ? '' : Number(e.target.value) }))}
            />
            <span>,</span>
            <input
              type="number" min="0"
              max={Number.isInteger(Number(tempValues.precision)) ? Number(tempValues.precision) : MAX_DECIMAL_PRECISION}
              value={tempValues.scale ?? ''}
              disabled={!editMode}
              title="Decimal places"
              onChange={e => setTempValues(prev => ({ ...prev, scale: e.target.value === '' ? '' : Number(e.target.value) }))}
            />
            {decimalProblem(tempValues) && (
              <span className={styles.decimal_problem}>{decimalProblem(tempValues)}</span>
            )}
          </div>
        )}
      </div>

      <div className={styles.record_col}>
        {!editMode ? (
          <span>
            {item?.default_value === "" || item?.default_value === null || item?.default_value === undefined ? "-" : String(item.default_value)}
          </span>
        ) : (
          <>
            {!(tempValues.ai && tempValues.type === "uint64_t" && tempValues.is_index) && 
             !(tempValues.type === "time_point" && (tempValues.is_auto_created || tempValues.is_auto_updated)) && (
              <DefaultRecordValueInput
                type={tempValues.type}
                value={tempValues.default_value}
                onChange={(val) =>
                  setTempValues((prev) => ({ ...prev, default_value: val }))
                }
              />
            )}
          </>
        )}
        {
          tempValues.type === "time_point" &&
          <RecordOptionSwitch
            disabled={!editMode}
            options={["ac", "au"]}
            value={tempValues.is_auto_created ? "ac" : tempValues.is_auto_updated ? "au" : null}
            onChange={(val) =>
              setTempValues((prev) => ({
                ...prev,
                is_auto_created: val === "ac",
                is_auto_updated: val === "au",
                // Reset default_value when AC or AU is selected
                default_value: (val === "ac" || val === "au") ? "" : prev.default_value,
              }))
            }
          />
        }
      </div>

      <div className={styles.record_tools}>
        {editMode ? (
          <>
            <button disabled={Object.values(isFieldValid).some(valid => !valid)} onClick={saveChanges}>Save</button>
            <button onClick={() => {
              setTempValues({
                name: item.name,
                type: item.type,
                default_value: item.default_value,
                is_index: item.is_index,
                ai: item.ai,
                                 is_array: item.is_array,
                 is_auto_created: item.is_auto_created || false,
                 is_auto_updated: item.is_auto_updated || false,
                 description: item.description || '',
              });
              setEditMode(false);
            }}>
              <CancelIcon />
            </button>
          </>
        ) : (
          <>
            <button onClick={() => setEditMode(true)}>Edit</button>
            <button
              disabled={!item?.new}
              title={!item?.new ? "Cannot delete existing field" : "Delete field"}
              onClick={() => onDelete(index)}
            >
              <TrashCanIcon />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default CompositionRecord;